import React from 'react';

import { ADVPRO_AMBASSADOR_FORM_URL } from 'constants/advproAmbassadorWebinar.constants';

// Diseño tomado de advocatespro-modal-ambassador-template.html (ADV-3170,
// webinar del rol Embajador, miércoles 2 de septiembre de 2026). Variante compacta
// de un panel (tarjeta de 480px) de la familia de "Modales Especiales" de
// AdvocatesPro; el modal del 5 de agosto es la variante horizontal de dos
// paneles. Íconos de Lucide como SVG inline, igual criterio que AdvproLaunchModal.

const PRIMARY = '#0061fe'; // $ap-primary-500
const SECONDARY = '#ee4a79'; // $ap-secondary-500
const SECONDARY_DARK = '#d9436e'; // $ap-secondary-600
const GREY_800 = '#222222';
const GREY_500 = '#3d3d3d';
const GREY_300 = '#7d7d7d';
const URBANIST = "'Urbanist', sans-serif";
const DMSANS = "'DM Sans', sans-serif";

const iconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const
};

const SparklesIcon = () => (
  <svg {...iconProps} width={14} height={14}>
    <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
    <path d="M20 3v4" />
    <path d="M22 5h-4" />
    <path d="M4 17v2" />
    <path d="M5 18H3" />
  </svg>
);

const UserIcon = () => (
  <svg {...iconProps} width={16} height={16}>
    <circle cx="12" cy="8" r="5" />
    <path d="M20 21a8 8 0 0 0-16 0" />
  </svg>
);

const TrophyIcon = () => (
  <svg {...iconProps} width={16} height={16}>
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
    <path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
  </svg>
);

const SmartphoneIcon = () => (
  <svg {...iconProps} width={16} height={16}>
    <rect width="14" height="20" x="5" y="2" rx="2" ry="2" />
    <path d="M12 18h.01" />
  </svg>
);

const ChartBarIcon = () => (
  <svg {...iconProps} width={16} height={16}>
    <path d="M3 3v18h18" />
    <path d="M18 17V9" />
    <path d="M13 17V5" />
    <path d="M8 17v-3" />
  </svg>
);

const TicketIcon = () => (
  <svg {...iconProps} width={18} height={18}>
    <path d="M2 9a3 3 0 1 0 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 1 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
    <path d="M13 5v2" />
    <path d="M13 17v2" />
    <path d="M13 11v2" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg {...iconProps} width={14} height={14}>
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

const CloseIcon = () => (
  <svg {...iconProps} width={18} height={18}>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

interface Feature {
  icon: () => JSX.Element;
  title: string;
  text: string;
  /** Fondo del círculo del ícono. */
  bg: string;
  /** Color del trazo del ícono. */
  fg: string;
}

const FEATURES: Feature[] = [
  {
    icon: UserIcon,
    title: 'Nuevo perfil del Embajador',
    text: 'Progreso, insignias y próximos retos.',
    bg: '#e6efff', // $ap-primary-50
    fg: PRIMARY
  },
  {
    icon: TrophyIcon,
    title: 'Gamificación y recompensas',
    text: 'Niveles y ranking en tiempo real.',
    bg: '#d1fae5',
    fg: '#065f46'
  },
  {
    icon: SmartphoneIcon,
    title: 'Publica en 3 clics',
    text: 'Desde la app móvil, en segundos.',
    bg: '#fdedf2', // $ap-secondary-50
    fg: SECONDARY
  },
  {
    icon: ChartBarIcon,
    title: 'Su reporte de impacto',
    text: 'Alcance, participación y recompensas.',
    bg: 'rgba(249, 198, 22, 0.16)',
    fg: '#92400e'
  }
];

/**
 * Horarios del webinar. El evento es a las 12:00pm de Colombia (UTC-5), así que
 * México (UTC-6) es 11:00am, Perú (UTC-5) 12:00pm, Venezuela y Chile (UTC-4)
 * 1:00pm y Argentina (UTC-3) 2:00pm. Nota: el mockup HTML adjunto a ADV-3170
 * traía "2:00 pm AR" y "7:00 pm ARG" en dos de los chips, que no coinciden con
 * los horarios de la descripción de la tarea ni con la conversión real de husos.
 * Acá van los correctos, iguales a los del modal del 5 de agosto.
 *
 * CUIDADO si el evento se vuelve a mover: Chile entra en horario de verano el
 * sábado 5 de septiembre de 2026 y pasa a UTC-3. A partir de esa fecha deja de
 * compartir horario con Venezuela y el chip "1:00 pm VE·CH" queda mal — habría
 * que separarlos en dos chips (VE 1:00 pm, CH 2:00 pm). El 2 de septiembre
 * todavía está antes del cambio, así que estos horarios siguen siendo válidos.
 */
const TIME_SLOTS: string[] = [
  '11:00 am MX',
  '12:00 pm CO·PE',
  '1:00 pm VE·CH',
  '2:00 pm ARG'
];

export interface AdvproAmbassadorModalProps {
  open: boolean;
  /** Cierre con la X o clic en el backdrop: cierra sin descartar (vuelve en la próxima carga). */
  onClose: () => void;
  /** Clic en "Ahora no": cierra sin descartar (vuelve en la próxima carga). */
  onDismiss: () => void;
  /** Clic en el CTA "Reserva mi lugar": abre el form en pestaña nueva y descarta para siempre. */
  onCta: () => void;
}

export const AdvproAmbassadorModal: React.FC<AdvproAmbassadorModalProps> = ({
  open,
  onClose,
  onDismiss,
  onCta
}) => {
  if (!open) return null;

  // El backdrop cierra sólo cuando el clic nace en el overlay mismo, no cuando
  // burbujea desde la tarjeta.
  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose();
  };

  return (
    <>
      <div
        className="advpro-amb-overlay"
        onClick={handleOverlayClick}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          padding: 20,
          animation: 'advproAmbFadeIn 0.2s ease'
        }}
      >
        {/* Wrapper: tarjeta y botón cerrar son dos hijos flex en fila. El botón
            no está posicionado absoluto a propósito — así no se corta en
            pantallas angostas. */}
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="advproAmbassadorTitle"
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 14,
            maxWidth: 528,
            width: '100%',
            animation: 'advproAmbScaleIn 0.25s ease'
          }}
        >
          {/* Tarjeta. overflow:hidden sólo redondea bordes; el scroll real vive
              únicamente en el body. */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              background: '#ffffff',
              borderRadius: 24,
              overflow: 'hidden',
              width: 480,
              maxWidth: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              maxHeight: 'calc(100vh - 40px)',
              fontFamily: DMSANS
            }}
          >
            {/* HEADER — fijo, nunca scrollea */}
            <div
              style={{
                background:
                  'linear-gradient(108.2deg, #b0ceff 0%, #ffffff 55%, #fac7d5 100%)',
                borderRadius: '24px 24px 0 0',
                padding: '32px 32px 24px',
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
                flexShrink: 0
              }}
            >
              <div
                style={{
                  display: 'inline-flex',
                  alignSelf: 'flex-start',
                  alignItems: 'center',
                  gap: 8,
                  height: 30,
                  borderRadius: 50,
                  border: `1px solid ${PRIMARY}`,
                  background: '#ffffff',
                  padding: '0 14px',
                  color: PRIMARY,
                  flexShrink: 0
                }}
              >
                <SparklesIcon />
                <span
                  style={{
                    fontFamily: URBANIST,
                    fontSize: 13,
                    fontWeight: 600,
                    color: PRIMARY,
                    whiteSpace: 'nowrap'
                  }}
                >
                  Webinar en vivo · Gratis
                </span>
              </div>

              {/* Título con degradado tricolor. El texto va en un solo nodo:
                  partirlo en spans puede perder el espacio entre palabras. */}
              <h2
                id="advproAmbassadorTitle"
                style={{
                  fontFamily: URBANIST,
                  fontSize: 25,
                  fontWeight: 700,
                  lineHeight: '29px',
                  letterSpacing: '-0.4px',
                  background:
                    'linear-gradient(to right, #0061fe 1.85%, #00296b 49.5%, #ee4a79 82.2%)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  margin: 0
                }}
              >
                El rol Embajador, reinventado en AdvocatesPro
              </h2>

              <p
                style={{
                  fontFamily: DMSANS,
                  fontSize: 14,
                  fontWeight: 400,
                  color: GREY_500,
                  lineHeight: '19px',
                  margin: 0
                }}
              >
                Continuamos la capacitación de la nueva versión con un webinar
                enfocado en cómo vive el{' '}
                <strong style={{ fontWeight: 700, color: GREY_800 }}>
                  Embajador
                </strong>{' '}
                la nueva AdvocatesPro.
              </p>
            </div>

            {/* BODY — único bloque que scrollea. flex:1 + minHeight:0 es el
                patrón completo: sin minHeight:0 el overflow no tiene efecto. */}
            <div
              className="advpro-amb-body"
              style={{
                padding: '24px 32px',
                display: 'flex',
                flexDirection: 'column',
                gap: 20,
                flex: 1,
                minHeight: 0,
                overflowY: 'auto'
              }}
            >
              {/* Agenda */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  background: '#fdedf2',
                  borderRadius: 16,
                  padding: '12px 16px'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 52,
                    height: 52,
                    borderRadius: 14,
                    background: SECONDARY,
                    color: '#ffffff',
                    flexShrink: 0
                  }}
                >
                  <span
                    style={{
                      fontFamily: URBANIST,
                      fontSize: 18,
                      fontWeight: 700,
                      lineHeight: 1
                    }}
                  >
                    2
                  </span>
                  <span
                    style={{
                      fontFamily: URBANIST,
                      fontSize: 10,
                      fontWeight: 600,
                      textTransform: 'uppercase'
                    }}
                  >
                    Sep
                  </span>
                </div>

                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6
                  }}
                >
                  <span
                    style={{
                      fontFamily: DMSANS,
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: '0.2px',
                      textTransform: 'uppercase',
                      color: '#b93c5e'
                    }}
                  >
                    Miércoles 2 de septiembre
                  </span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {TIME_SLOTS.map(slot => (
                      <span
                        key={slot}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '3px 8px',
                          borderRadius: 6,
                          border: '1px solid #f7acc1',
                          background: '#ffffff',
                          fontFamily: DMSANS,
                          fontSize: 11,
                          fontWeight: 700,
                          color: '#8a2c48',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {slot}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Features */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                  padding: 16,
                  background: '#fafafa',
                  borderRadius: 16
                }}
              >
                {FEATURES.map(feature => {
                  const Icon = feature.icon;
                  return (
                    <div
                      key={feature.title}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10
                      }}
                    >
                      <div
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: '50%',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          background: feature.bg,
                          color: feature.fg
                        }}
                      >
                        <Icon />
                      </div>
                      <div
                        style={{
                          flex: 1,
                          minWidth: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                          paddingTop: 4
                        }}
                      >
                        <h4
                          style={{
                            fontFamily: URBANIST,
                            fontSize: 14,
                            fontWeight: 600,
                            color: GREY_800,
                            margin: 0
                          }}
                        >
                          {feature.title}
                        </h4>
                        <p
                          style={{
                            fontFamily: DMSANS,
                            fontSize: 13,
                            fontWeight: 400,
                            color: GREY_300,
                            lineHeight: '17px',
                            margin: 0
                          }}
                        >
                          {feature.text}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Nota de cupos */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '14px 16px',
                  borderRadius: 12,
                  background:
                    'linear-gradient(108.2deg, rgba(230, 239, 255, 0.7) 0%, #ffffff 50.75%, rgba(253, 237, 242, 0.7) 100%)',
                  color: PRIMARY
                }}
              >
                <span style={{ display: 'flex', flexShrink: 0 }}>
                  <TicketIcon />
                </span>
                <p
                  style={{
                    fontFamily: DMSANS,
                    fontSize: 13,
                    fontWeight: 500,
                    color: '#00358c',
                    lineHeight: 1.4,
                    margin: 0
                  }}
                >
                  Cupos limitados. Si no puedes en vivo, te enviamos la
                  grabación.
                </p>
              </div>
            </div>

            {/* FOOTER — fijo, nunca scrollea */}
            <div
              className="advpro-amb-footer"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                background: '#ffffff',
                borderTop: '1px solid #ececec',
                borderRadius: '0 0 24px 24px',
                padding: '16px 32px 20px',
                flexShrink: 0
              }}
            >
              <button
                type="button"
                className="advpro-amb-btn-later"
                onClick={onDismiss}
              >
                Ahora no
              </button>
              <a
                className="advpro-amb-btn-cta"
                href={ADVPRO_AMBASSADOR_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onCta}
              >
                <ArrowRightIcon />
                Reserva mi lugar
              </a>
            </div>
          </div>

          <button
            type="button"
            className="advpro-amb-close-btn"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <CloseIcon />
          </button>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Urbanist:wght@600;700&family=DM+Sans:wght@400;500;700&display=swap');
        @keyframes advproAmbFadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes advproAmbScaleIn { from { transform: scale(0.95); opacity: 0 } to { transform: scale(1); opacity: 1 } }
        .advpro-amb-close-btn{width:34px;height:34px;border-radius:50%;background:#ffffff;border:none;cursor:pointer;color:${GREY_500};display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;padding:0;box-shadow:0 2px 8px rgba(0,0,0,0.1);transition:background-color 0.2s ease;}
        .advpro-amb-close-btn:hover{background:#fafafa;}
        .advpro-amb-close-btn:focus-visible{outline:2px solid ${PRIMARY};outline-offset:2px;}
        .advpro-amb-btn-later{flex:1;display:flex;align-items:center;justify-content:center;height:44px;padding:0 16px;border-radius:12px;border:1px solid #c3c3c3;cursor:pointer;background:#ffffff;color:${GREY_300};font-family:${URBANIST};font-size:14px;font-weight:700;white-space:nowrap;transition:background-color 0.2s ease;}
        .advpro-amb-btn-later:hover{background:#fafafa;}
        .advpro-amb-btn-later:focus-visible{outline:2px solid ${PRIMARY};outline-offset:2px;}
        .advpro-amb-btn-cta{flex:2;display:flex;align-items:center;justify-content:center;gap:8px;height:44px;padding:0 16px;border-radius:12px;border:none;cursor:pointer;background:${SECONDARY};color:#ffffff;font-family:${URBANIST};font-size:14px;font-weight:700;white-space:nowrap;text-decoration:none;transition:background-color 0.2s ease;}
        .advpro-amb-btn-cta:hover{background:${SECONDARY_DARK};}
        .advpro-amb-btn-cta:focus-visible{outline:2px solid ${SECONDARY_DARK};outline-offset:3px;}
        @media (max-width: 480px) {
          .advpro-amb-footer { flex-direction: column; }
          .advpro-amb-btn-later, .advpro-amb-btn-cta { flex: 1; width: 100%; }
        }
      `}</style>
    </>
  );
};

export default AdvproAmbassadorModal;
